/**
 * Parse transaction errors from Anchor/Solana into typed error objects.
 * Uses @morfi/errors for code-to-name mapping -- zero hardcoded error codes.
 *
 * Disambiguation: markets and cp-swap (graduated-market AMM) share the
 * 6000+ custom-code range. Where the error logs expose the originating
 * program id (the runtime's `Program <id> failed: custom program error: 0x..`
 * line), we attribute the code to that program; otherwise callers may pass a
 * `programIdHint`. See specs/016-tx-error-pipeline/spec.md.
 */
import { type ErrorProgram, resolveErrorByCode } from './index.js';

export interface MorfiError {
  code: number;
  name: string;
  msg: string;
  retryable: boolean;
  /** Which on-chain program the code resolved against. */
  program: ErrorProgram;
}

export interface ParseOptions {
  /** Fallback program id (base58) used when the logs don't carry one. */
  programIdHint?: string;
}

interface ExtractedError {
  code: number;
  /** base58 program id of the originating program, when the logs expose it. */
  programId?: string;
}

/**
 * Parse a transaction error into a typed MorfiError.
 * Returns null if the error is not a recognized program error.
 */
// ALLOW-UNKNOWN: error parameter must accept unknown types for safe error parsing
export function parseTransactionError(error: unknown, opts?: ParseOptions): MorfiError | null {
  const extracted = extractError(error);
  if (extracted === null) {
    return null;
  }
  const programId = extracted.programId ?? opts?.programIdHint;
  const resolved = resolveErrorByCode(extracted.code, programId);
  if (!resolved) {
    return null;
  }
  return {
    code: extracted.code,
    name: resolved.name,
    msg: resolved.msg,
    retryable: false, // All program errors are non-retryable
    program: resolved.program,
  };
}

/**
 * Runtime line emitted by the failing program, carrying BOTH the program id and
 * the custom code in hex, e.g.
 *   "Program morfijP3..GC failed: custom program error: 0x176d"
 * The innermost (originating) program logs this first, so the FIRST match wins
 * even across CPI boundaries.
 */
const PROGRAM_FAILURE_REGEX =
  /Program ([1-9A-HJ-NP-Za-km-z]{32,44}) failed: custom program error: 0x([0-9a-fA-F]+)/;

/** Anchor error log line pattern: "Program log: AnchorError ... Error Number: 6042" */
const ANCHOR_ERROR_REGEX = /Error Number:\s*(\d+)/;

/** Bare "custom program error: 0x.." with no program id (some RPC providers). */
const HEX_CUSTOM_ERROR_REGEX = /custom program error:\s*0x([0-9a-fA-F]+)/;

/**
 * Extract an error code (and originating program id when available) from
 * various error shapes:
 * - Errors with `logs` array (program-failure line, Anchor pattern, hex)
 * - Errors with `error.errorCode.number` (Anchor direct)
 * - Errors with `InstructionError[1].Custom` (Solana RPC format)
 */
// ALLOW-UNKNOWN: error parameter must accept unknown types for safe code extraction
function extractError(error: unknown): ExtractedError | null {
  if (error === null || error === undefined) {
    return null;
  }

  if (typeof error !== 'object') {
    return null;
  }

  // Shape 1: error.logs array (SendTransactionError) — most informative.
  const fromLogs = extractFromLogs(error);
  if (fromLogs !== null) {
    return fromLogs;
  }

  const fromMessage = extractFromMessage(error);
  if (fromMessage !== null) {
    return fromMessage;
  }

  // Shape 2: error.error.errorCode.number (Anchor direct)
  const fromAnchorDirect = extractFromAnchorDirect(error);
  if (fromAnchorDirect !== null) {
    return { code: fromAnchorDirect };
  }

  // Shape 3: error.InstructionError[1].Custom (Solana RPC)
  const fromRpc = extractFromInstructionError(error);
  if (fromRpc !== null) {
    return { code: fromRpc };
  }

  return null;
}

/** Scan error.logs for the program-failure line, Anchor pattern, then bare hex. */
function extractFromLogs(error: object): ExtractedError | null {
  if (!('logs' in error)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from unknown error object to extract logs field
  const logsField = (error as { logs: unknown }).logs;
  if (!Array.isArray(logsField)) {
    return null;
  }

  return extractFromLogLines(logsField);
}

/**
 * A `SendTransactionError` re-thrown as a plain `Error` keeps the simulation
 * logs only inside `message` ("Logs: [ ... ]", one entry per line), so the
 * same line patterns apply to the message text.
 */
function extractFromMessage(error: object): ExtractedError | null {
  if (!('message' in error)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from unknown error object to extract message field
  const message = (error as { message: unknown }).message;
  if (typeof message !== 'string' || message.length === 0) {
    return null;
  }

  return extractFromLogLines(message.split('\n'));
}

// ALLOW-UNKNOWN: RPC error log arrays are untyped and may mix strings with other values
function extractFromLogLines(lines: readonly unknown[]): ExtractedError | null {
  let anchorNumberCode: number | null = null;
  let bareHexCode: number | null = null;

  for (const entry of lines) {
    if (typeof entry !== 'string') {
      continue;
    }

    // Best signal: program id + code together. First match = originating program.
    const failure = PROGRAM_FAILURE_REGEX.exec(entry);
    if (failure) {
      const programId = failure[1];
      const hex = failure[2];
      if (programId !== undefined && hex !== undefined) {
        return { code: Number.parseInt(hex, 16), programId };
      }
    }

    // Fallbacks (no program id): remember the first of each, keep scanning for a
    // program-failure line which is strictly better.
    if (anchorNumberCode === null) {
      const anchor = ANCHOR_ERROR_REGEX.exec(entry);
      const codeStr = anchor?.[1];
      if (codeStr !== undefined) {
        anchorNumberCode = Number(codeStr);
      }
    }
    if (bareHexCode === null) {
      const hexOnly = HEX_CUSTOM_ERROR_REGEX.exec(entry);
      const hexStr = hexOnly?.[1];
      if (hexStr !== undefined) {
        bareHexCode = Number.parseInt(hexStr, 16);
      }
    }
  }

  if (anchorNumberCode !== null) {
    return { code: anchorNumberCode };
  }
  if (bareHexCode !== null) {
    return { code: bareHexCode };
  }
  return null;
}

/** Extract from error.error.errorCode.number (Anchor SDK shape). */
function extractFromAnchorDirect(error: object): number | null {
  if (!('error' in error)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from unknown error object to extract nested error field
  const inner = (error as { error: unknown }).error;
  if (typeof inner !== 'object' || inner === null) {
    return null;
  }

  if (!('errorCode' in inner)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from nested unknown to extract errorCode field
  const errorCode = (inner as { errorCode: unknown }).errorCode;
  if (typeof errorCode !== 'object' || errorCode === null) {
    return null;
  }

  if (!('number' in errorCode)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from errorCode to extract number field
  const num = (errorCode as { number: unknown }).number;
  if (typeof num !== 'number') {
    return null;
  }

  return num;
}

/** Extract from InstructionError[1].Custom (Solana RPC shape). */
function extractFromInstructionError(error: object): number | null {
  if (!('InstructionError' in error)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from unknown error object to extract InstructionError field
  const instructionError = (error as { InstructionError: unknown }).InstructionError;
  if (!Array.isArray(instructionError) || instructionError.length < 2) {
    return null;
  }

  const detail = instructionError[1];
  if (typeof detail !== 'object' || detail === null) {
    return null;
  }

  if (!('Custom' in detail)) {
    return null;
  }

  // ALLOW-UNKNOWN: narrowing from detail to extract Custom field
  const custom = (detail as { Custom: unknown }).Custom;
  if (typeof custom !== 'number') {
    return null;
  }

  return custom;
}
